import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Loader2, Radio, Usb, HelpCircle } from 'lucide-react'
import { PlaylistTrack } from '@/types/playlist'
import { VolumeCurve, EQCurve, FilterType } from '@/components/TransitionControls'
import { PlaygroundTransitionPlayer } from '@/components/PlaygroundTransitionPlayer'
import { AppTutorial, TutorialStep } from '@/components/AppTutorial'
import { getAlbumCoverUrl } from '@/utils/imageUtils'
import { getApiBaseUrl } from '@/services/trackService'
import { toCamelotKey } from '@/utils/playlistUtils'
import * as Tone from 'tone'

// 转场预设样式
type TransitionPreset = 'auto' | 'fade' | 'rise' | 'blend' | 'wave' | 'melt' | 'slam'

interface TransitionEditorPageProps {}

interface LocationState {
  fromTrack: PlaylistTrack
  toTrack: PlaylistTrack
  transitionDuration?: number
}

export const TransitionEditorPage: React.FC<TransitionEditorPageProps> = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const state = location.state as LocationState | null
  
  // 状态管理
  const [transitionDuration, setTransitionDuration] = useState(8) // 默认8秒
  const [selectedPreset, setSelectedPreset] = useState<TransitionPreset>('auto')
  const [volumeCurve, setVolumeCurve] = useState<VolumeCurve>('smooth-crossfade')
  const [eqCurve, setEQCurve] = useState<EQCurve>('center-bass-swap')
  const [filterType, setFilterType] = useState<FilterType>('none')
  
  // Offset 状态 - 用于记录播放器中的offset调整
  const [track1OffsetBeats, setTrack1OffsetBeats] = useState(0)
  const [track2OffsetBeats, setTrack2OffsetBeats] = useState(0)
  const [transitionOffsetBeats, setTransitionOffsetBeats] = useState(0)
  
  // nbeats 状态 - 用于控制转场长度
  const [currentNbeats, setCurrentNbeats] = useState<number>(32) // 默认 8 bars * 4 beats = 32 beats
  
  // Playground mode test状态
  const [playgroundTestResult, setPlaygroundTestResult] = useState<string | null>(null)
  const [playgroundTestError, setPlaygroundTestError] = useState<string | null>(null)
  const [transitionId, setTransitionId] = useState<string | null>(null)
  const [outputDir, setOutputDir] = useState<string | null>(null)
  const [isLoadingTransition, setIsLoadingTransition] = useState(true)
  const [isFromCache, setIsFromCache] = useState(false)
  
  // Playground 音频和同步信息
  const [playgroundAudioFiles, setPlaygroundAudioFiles] = useState<{
    prevtrack: string | null
    nexttrack: string | null
  }>({ prevtrack: null, nexttrack: null })
  const [syncInfo, setSyncInfo] = useState<{
    nbeats: number
    track_next_start_beat: number
    track_prev_bpm: number
  } | null>(null)
  
  // Tone.js 效果器状态 - 可通过 MIDI 输入控制
  const [track1EQ, setTrack1EQ] = useState({ low: 0, mid: 0, high: 0 })
  const [track2EQ, setTrack2EQ] = useState({ low: 0, mid: 0, high: 0 })
  const [track1Filter, setTrack1Filter] = useState(50) // 0-50: lowpass, 50-100: highpass, 50 = 中性
  const [track2Filter, setTrack2Filter] = useState(50)
  const [track1Volume, setTrack1Volume] = useState(0) // dB, 0 = 默认音量
  const [track2Volume, setTrack2Volume] = useState(0)
  
  // Tone.js 效果器节点引用
  const track1EQRef = useRef<Tone.EQ3 | null>(null)
  const track2EQRef = useRef<Tone.EQ3 | null>(null)
  const track1PlayerRef = useRef<Tone.Player | null>(null)
  const track2PlayerRef = useRef<Tone.Player | null>(null)
  const isAudioInitialized = useRef(false)
  
  // MIDI 状态管理
  const [midiSupported, setMidiSupported] = useState(false)
  const [midiInputs, setMidiInputs] = useState<any[]>([])
  const [midiOutputs, setMidiOutputs] = useState<any[]>([])
  const [selectedInput, setSelectedInput] = useState<string | null>(null)
  const [selectedOutput, setSelectedOutput] = useState<string | null>(null)
  const [midiConnected, setMidiConnected] = useState(false)
  const [showMidiSettings, setShowMidiSettings] = useState(false)
  const [midiEnabled, setMidiEnabled] = useState(true)  // 默认启用 MIDI 输出
  const [midiInputEnabled, setMidiInputEnabled] = useState(false)  // 默认禁用 MIDI 输入
  
  const midiAccessRef = useRef<any>(null)
  const selectedOutputRef = useRef<any>(null)
  
  // 教程状态
  const [isTutorialOpen, setIsTutorialOpen] = useState(false)
  
  // MIDI CC 映射（与 MidiControllerPage 保持一致）
  const CC_MAP = {
    channel1: {
      fxLevel: 102,      // Deck 0 FX (未使用)
      eqHigh: 103,       // Deck 0 EQHigh
      eqMid: 104,        // Deck 0 EQMid
      eqLow: 105,        // Deck 0 EQLow
      filter: 106,       // Deck 0 FilterCutoff
      volume: 112        // Deck 0 Volume
    },
    channel2: {
      fxLevel: 107,      // Deck 1 FX (未使用)
      eqHigh: 108,       // Deck 1 EQHigh
      eqMid: 109,        // Deck 1 EQMid
      eqLow: 110,        // Deck 1 EQLow
      filter: 111,       // Deck 1 FilterCutoff
      volume: 113        // Deck 1 Volume
    }
  }
  
  // 检查是否有传入的轨道数据
  if (!state || !state.fromTrack || !state.toTrack) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            没有转场数据
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            请从歌单编辑页面选择两首歌曲进行转场编辑
          </p>
          <Link 
            to="/playground"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
          >
            <ArrowLeft size={20} />
            返回歌单
          </Link>
        </div>
      </div>
    )
  }
  
  const { fromTrack, toTrack } = state
  
  // 教程步骤定义
  const tutorialSteps: TutorialStep[] = [
    {
      title: '欢迎使用转场编辑器 🎛️',
      description: '这是一个专业的DJ转场编辑工具，可以精确控制两首歌曲之间的混音效果。支持实时调整音量、EQ、滤波器等参数，还能通过MIDI设备进行硬件控制！',
      position: 'center',
    },
    {
      title: 'MIDI 连接 - 硬件控制',
      description: '点击这里可以连接MIDI设备（如DJ控制器）。支持输入控制（手动调整效果）和输出控制（自动发送Envelope数据）。连接后可以使用CC114切换自动/手动模式。',
      targetSelector: '[data-tutorial="midi-settings"]',
      position: 'bottom',
      highlightPadding: 12,
    },
    {
      title: '波形播放器 - 实时预览',
      description: '这里显示两首歌的波形。播放时可以实时看到音量、EQ、滤波器的变化曲线。支持拖动调整offset，精确控制转场起始点。绿色区域是转场混音段。',
      targetSelector: '[data-tutorial="waveform-player"]',
      position: 'bottom',
      highlightPadding: 16,
    },
    {
      title: '转场长度 - 调整混音时长',
      description: '选择转场的长度（以小节计）。1-8 bars可选，系统会根据歌曲BPM自动计算实际秒数。绿色高亮显示当前选中的长度。',
      targetSelector: '[data-tutorial="duration-control"]',
      position: 'top',
      highlightPadding: 12,
    },
    {
      title: '高级控制 - 精细调整效果',
      description: '这三个下拉菜单控制转场的核心效果：Volume（音量渐变曲线）、EQ（均衡器变化）、Filter（滤波器效果）。每个都有多种预设可选，适配不同风格的转场。',
      targetSelector: '[data-tutorial="advanced-controls"]',
      position: 'top',
      highlightPadding: 16,
    },
    {
      title: '转场预设 - 一键应用',
      description: '这些是预设的转场风格组合。Auto会根据两首歌的BPM差异智能选择，其他如Fade、Rise、Blend等都是精心设计的专业转场方案。点击即可快速应用。',
      targetSelector: '[data-tutorial="presets"]',
      position: 'top',
      highlightPadding: 16,
    },
    {
      title: '保存转场 - 保留你的设置',
      description: '调整满意后，点击这里保存所有参数（包括offset、nbeats、curve等）。下次打开相同的歌曲对会自动加载你保存的设置。',
      targetSelector: '[data-tutorial="save-button"]',
      position: 'bottom',
      highlightPadding: 12,
    },
    {
      title: '开始创作吧！🎵',
      description: '现在你已经掌握了所有功能。试试播放、调整参数、连接MIDI设备，创作出完美的DJ转场效果吧！',
      position: 'center',
    },
  ]
  
  // 初始化 Tone.js 效果器链
  useEffect(() => {
    const initializeAudio = async () => {
      if (isAudioInitialized.current) return
      
      try {
        // 确保音频上下文已启动
        await Tone.start()
        console.log('🎵 Tone.js Audio Context started')
        
        // 创建 EQ3 效果器节点
        track1EQRef.current = new Tone.EQ3({
          low: track1EQ.low,
          mid: track1EQ.mid,
          high: track1EQ.high,
          lowFrequency: 400,
          highFrequency: 2500,
        }).toDestination()
        
        track2EQRef.current = new Tone.EQ3({
          low: track2EQ.low,
          mid: track2EQ.mid,
          high: track2EQ.high,
          lowFrequency: 400,
          highFrequency: 2500,
        }).toDestination()
        
        console.log('✅ EQ 效果器已初始化')
        isAudioInitialized.current = true
      } catch (error) {
        console.error('❌ 初始化音频效果器失败:', error)
      }
    }
    
    initializeAudio()
    
    // 清理函数
    return () => {
      track1EQRef.current?.dispose()
      track2EQRef.current?.dispose()
      track1PlayerRef.current?.dispose()
      track2PlayerRef.current?.dispose()
    }
  }, [])
  
  // 更新轨道1的EQ参数
  useEffect(() => {
    if (track1EQRef.current) {
      track1EQRef.current.low.value = track1EQ.low
      track1EQRef.current.mid.value = track1EQ.mid
      track1EQRef.current.high.value = track1EQ.high
    }
  }, [track1EQ])
  
  // 更新轨道2的EQ参数
  useEffect(() => {
    if (track2EQRef.current) {
      track2EQRef.current.low.value = track2EQ.low
      track2EQRef.current.mid.value = track2EQ.mid
      track2EQRef.current.high.value = track2EQ.high
    }
  }, [track2EQ])
  
  // 初始化 MIDI
  useEffect(() => {
    console.log('🎹 检查 Web MIDI API 支持...')
    const nav = navigator as any
    
    if (nav.requestMIDIAccess) {
      setMidiSupported(true)
      console.log('✅ Web MIDI API 支持，开始初始化...')
      initMIDI()
    } else {
      console.error('❌ 浏览器不支持 Web MIDI API')
    }
    
    return () => {
      // 清理 MIDI 连接
      if (selectedOutputRef.current) {
        selectedOutputRef.current.close()
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  
  const initMIDI = async () => {
    try {
      console.log('🔄 正在请求 MIDI 访问权限...')
      const nav = navigator as any
      if (!nav.requestMIDIAccess) {
        throw new Error('浏览器不支持 Web MIDI API')
      }
      
      const access = await nav.requestMIDIAccess()
      midiAccessRef.current = access
      
      console.log('✅ MIDI 访问权限已授予')
      console.log('📤 输出设备数量:', access.outputs.size)
      
      // 列出所有输出设备
      access.outputs.forEach((output: any) => {
        console.log('  输出设备:', output.name, '| 状态:', output.state, '| ID:', output.id)
      })
      
      // 更新设备列表
      updateMIDIDevices(access)
      
      // 监听设备连接/断开
      access.onstatechange = (event: any) => {
        console.log('🔌 MIDI 设备状态变化:', event.port.name, event.port.state)
        updateMIDIDevices(access)
      }
      
      console.log('✅ MIDI 初始化成功')
    } catch (error) {
      console.error('❌ MIDI 初始化失败:', error)
    }
  }
  
  const updateMIDIDevices = (access: any) => {
    const inputs: any[] = []
    const outputs: any[] = []
    
    console.log('🔍 扫描 MIDI 设备...')
    
    access.inputs.forEach((input: any) => {
      console.log(`  检查输入: ${input.name} - 状态: ${input.state}`)
      if (input.state === 'connected') {
        inputs.push(input)
        console.log(`    ✅ 添加到可用输入列表`)
      }
    })
    
    access.outputs.forEach((output: any) => {
      console.log(`  检查输出: ${output.name} - 状态: ${output.state}`)
      if (output.state === 'connected') {
        outputs.push(output)
        console.log(`    ✅ 添加到可用输出列表`)
      }
    })
    
    console.log(`📊 找到 ${inputs.length} 个输入设备, ${outputs.length} 个输出设备`)
    
    setMidiInputs(inputs)
    setMidiOutputs(outputs)
    
    // 自动选择输入设备
    if (inputs.length > 0 && !selectedInput) {
      const em4eInput = inputs.find(input => input.name.includes('EM-4E'))
      const targetInput = em4eInput || inputs[0]
      console.log(`🎯 自动选择输入设备: ${targetInput.name}${em4eInput ? ' (优先 EM-4E)' : ''}`)
      setSelectedInput(targetInput.id)
      connectMIDIInput(targetInput)
    }
    
    // 自动选择输出设备
    if (outputs.length > 0 && !selectedOutput) {
      const em4eOutput = outputs.find(output => output.name.includes('EM-4E'))
      const targetOutput = em4eOutput || outputs[0]
      console.log(`🎯 自动选择输出设备: ${targetOutput.name}${em4eOutput ? ' (优先 EM-4E)' : ''}`)
      setSelectedOutput(targetOutput.id)
      connectMIDIOutput(targetOutput)
    }
  }
  
  const handleInputChange = (inputId: string) => {
    setSelectedInput(inputId)
    const input = midiInputs.find(inp => inp.id === inputId)
    if (input) {
      connectMIDIInput(input)
    }
  }
  
  const handleOutputChange = (outputId: string) => {
    setSelectedOutput(outputId)
    const output = midiOutputs.find(out => out.id === outputId)
    if (output) {
      connectMIDIOutput(output)
    }
  }
  
  const connectMIDIInput = (input: any) => {
    console.log(`🔗 正在连接到 MIDI 输入: ${input.name}`)
    
    // 移除之前的监听
    if (midiAccessRef.current) {
      midiAccessRef.current.inputs.forEach((inp: any) => {
        inp.onmidimessage = null
      })
    }
    
    // 打开输入端口
    input.open().then(() => {
      console.log(`✅ MIDI 输入端口已打开: ${input.name}`)
      // 添加消息监听
      input.onmidimessage = handleMIDIMessage
      console.log('✅ 已连接到 MIDI 输入，开始监听消息')
    }).catch((err: any) => {
      console.error('❌ 打开 MIDI 输入端口失败:', err)
    })
  }
  
  const handleMIDIMessage = useCallback((event: any) => {
    const [status, data1, data2] = event.data
    const command = status >> 4
    const channel = status & 0x0f
    
    // 只处理 Control Change 消息
    if (command !== 0xb) return
    
    const cc = data1
    const value = data2
    
    // CC114: 模式切换控制（始终响应，即使 midiInputEnabled 为 false）
    // 127 = 自动模式（启用输出，禁用输入）
    // 0 = 手动模式（启用输入，禁用输出）
    if (cc === 114) {
      const isAutoMode = value === 127
      setMidiEnabled(isAutoMode)
      setMidiInputEnabled(!isAutoMode)
      console.log(`🎛️ CC114 模式切换: ${isAutoMode ? '自动模式 (输出启用)' : '手动模式 (输入启用)'} (value=${value})`)
      return
    }
    
    // 其他 CC 消息只在输入启用时处理
    if (!midiInputEnabled) return
    
    console.log(`📨 MIDI 输入: CC${cc} = ${value}`)
    
    // 根据 CC 号更新对应的参数
    updateParameterFromMIDI(cc, value)
  }, [midiInputEnabled])
  
  const updateParameterFromMIDI = (cc: number, value: number) => {
    // Track 1 (Deck 0)
    if (cc === CC_MAP.channel1.volume) {
      // MIDI 0-127 → -38dB to -6dB
      const volumeDb = (value / 127) * 32 - 38
      setTrack1Volume(volumeDb)
      console.log(`🎚️ Track 1 Volume: ${volumeDb.toFixed(1)}dB`)
    } else if (cc === CC_MAP.channel1.filter) {
      // MIDI 0-127 → 0-100
      const filterValue = (value / 127) * 100
      setTrack1Filter(filterValue)
      console.log(`🎛️ Track 1 Filter: ${filterValue.toFixed(1)}`)
    } else if (cc === CC_MAP.channel1.eqLow) {
      // MIDI 0-64 → -24dB to 0dB (64 = 中央点 = 0dB)
      const eqDb = (value / 64) * 24 - 24
      setTrack1EQ(prev => ({ ...prev, low: eqDb }))
      console.log(`🎚️ Track 1 EQ Low: ${eqDb.toFixed(1)}dB`)
    } else if (cc === CC_MAP.channel1.eqMid) {
      const eqDb = (value / 64) * 24 - 24
      setTrack1EQ(prev => ({ ...prev, mid: eqDb }))
      console.log(`🎚️ Track 1 EQ Mid: ${eqDb.toFixed(1)}dB`)
    } else if (cc === CC_MAP.channel1.eqHigh) {
      const eqDb = (value / 64) * 24 - 24
      setTrack1EQ(prev => ({ ...prev, high: eqDb }))
      console.log(`🎚️ Track 1 EQ High: ${eqDb.toFixed(1)}dB`)
    }
    // Track 2 (Deck 1)
    else if (cc === CC_MAP.channel2.volume) {
      const volumeDb = (value / 127) * 32 - 38
      setTrack2Volume(volumeDb)
      console.log(`🎚️ Track 2 Volume: ${volumeDb.toFixed(1)}dB`)
    } else if (cc === CC_MAP.channel2.filter) {
      const filterValue = (value / 127) * 100
      setTrack2Filter(filterValue)
      console.log(`🎛️ Track 2 Filter: ${filterValue.toFixed(1)}`)
    } else if (cc === CC_MAP.channel2.eqLow) {
      // MIDI 0-64 → -24dB to 0dB (64 = 中央点 = 0dB)
      const eqDb = (value / 64) * 24 - 24
      setTrack2EQ(prev => ({ ...prev, low: eqDb }))
      console.log(`🎚️ Track 2 EQ Low: ${eqDb.toFixed(1)}dB`)
    } else if (cc === CC_MAP.channel2.eqMid) {
      const eqDb = (value / 64) * 24 - 24
      setTrack2EQ(prev => ({ ...prev, mid: eqDb }))
      console.log(`🎚️ Track 2 EQ Mid: ${eqDb.toFixed(1)}dB`)
    } else if (cc === CC_MAP.channel2.eqHigh) {
      const eqDb = (value / 64) * 24 - 24
      setTrack2EQ(prev => ({ ...prev, high: eqDb }))
      console.log(`🎚️ Track 2 EQ High: ${eqDb.toFixed(1)}dB`)
    }
  }
  
  const connectMIDIOutput = (output: any) => {
    console.log(`🔗 正在连接到 MIDI 输出: ${output.name}`)
    
    // 显式打开输出端口
    output.open().then(() => {
      console.log(`✅ MIDI 输出端口已打开: ${output.name}`)
      selectedOutputRef.current = output
      setMidiConnected(true)
      
      // 发送一个测试消息确认连接 (CC127 = 0)
      console.log('📤 发送测试消息确认连接...')
      try {
        output.send([0xb0, 127, 0])
        console.log('✅ 测试消息已发送，MIDI 输出就绪')
      } catch (error) {
        console.error('❌ 测试消息发送失败:', error)
      }
    }).catch((err: any) => {
      console.error('❌ 打开 MIDI 输出端口失败:', err)
    })
  }
  
  const sendMIDI = (cc: number, value: number, channel: number = 0) => {
    if (!midiEnabled || !selectedOutputRef.current) {
      return
    }
    
    const roundedValue = Math.round(Math.max(0, Math.min(127, value)))
    
    try {
      const status = 0xb0 | channel // Control Change on channel
      selectedOutputRef.current.send([status, cc, roundedValue])
    } catch (error) {
      console.error('❌ 发送 MIDI 消息失败:', error)
    }
  }
  
  // 处理 Envelope 更新并发送 MIDI
  const handleEnvelopeUpdate = useCallback((values: {
    track1: {
      volume: number
      filter: number
      eqLow: number
      eqMid: number
      eqHigh: number
    }
    track2: {
      volume: number
      filter: number
      eqLow: number
      eqMid: number
      eqHigh: number
    }
  }) => {
    if (!midiEnabled || !selectedOutputRef.current) {
      return
    }
    
    // Track 1 (Deck 0)
    // Volume: -38dB to -6dB 映射到 0-127
    const track1VolumeMidi = Math.round(((values.track1.volume + 38) / 32) * 127)
    sendMIDI(CC_MAP.channel1.volume, track1VolumeMidi)
    
    // Filter: 0-100 映射到 0-127
    const track1FilterMidi = Math.round((values.track1.filter / 100) * 127)
    sendMIDI(CC_MAP.channel1.filter, track1FilterMidi)
    
    // EQ: -24dB to 0dB 映射到 0-64 (0dB = 64中央点, -24dB = 0)
    const track1EQLowMidi = Math.round(((values.track1.eqLow + 24) / 24) * 64)
    const track1EQMidMidi = Math.round(((values.track1.eqMid + 24) / 24) * 64)
    const track1EQHighMidi = Math.round(((values.track1.eqHigh + 24) / 24) * 64)
    sendMIDI(CC_MAP.channel1.eqLow, track1EQLowMidi)
    sendMIDI(CC_MAP.channel1.eqMid, track1EQMidMidi)
    sendMIDI(CC_MAP.channel1.eqHigh, track1EQHighMidi)
    
    // Track 2 (Deck 1)
    const track2VolumeMidi = Math.round(((values.track2.volume + 38) / 32) * 127)
    sendMIDI(CC_MAP.channel2.volume, track2VolumeMidi)
    
    const track2FilterMidi = Math.round((values.track2.filter / 100) * 127)
    sendMIDI(CC_MAP.channel2.filter, track2FilterMidi)
    
    const track2EQLowMidi = Math.round(((values.track2.eqLow + 24) / 24) * 64)
    const track2EQMidMidi = Math.round(((values.track2.eqMid + 24) / 24) * 64)
    const track2EQHighMidi = Math.round(((values.track2.eqHigh + 24) / 24) * 64)
    sendMIDI(CC_MAP.channel2.eqLow, track2EQLowMidi)
    sendMIDI(CC_MAP.channel2.eqMid, track2EQMidMidi)
    sendMIDI(CC_MAP.channel2.eqHigh, track2EQHighMidi)
  }, [midiEnabled])
  
  // 页面加载时检查缓存并生成转场，并尝试加载保存的参数
  useEffect(() => {
    const loadOrGenerateTransition = async () => {
      setIsLoadingTransition(true)
      setPlaygroundTestError(null)
      
      try {
        // 从 URL 或 filename 中提取 hash
        const hashPrev = fromTrack.filename.replace(/\.(mp3|wav|flac)$/i, '')
        const hashNext = toTrack.filename.replace(/\.(mp3|wav|flac)$/i, '')
        
        console.log('🔍 Checking transition cache:', { hashPrev, hashNext })
        
        // 先检查缓存
        const cacheResponse = await fetch(`${getApiBaseUrl()}/api/transition-editor/check-cache`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            hashPrev,
            hashNext,
          })
        })
        
        if (!cacheResponse.ok) {
          throw new Error('Failed to check cache')
        }
        
        const cacheData = await cacheResponse.json()
        console.log('📦 Cache check result:', cacheData)
        
        // 尝试加载保存的参数
        let savedParams: any = null
        try {
          const paramsResponse = await fetch(`${getApiBaseUrl()}/api/transition-editor/load-params`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              hashPrev,
              hashNext,
            })
          })
          
          if (paramsResponse.ok) {
            const paramsData = await paramsResponse.json()
            if (paramsData.found && paramsData.params) {
              console.log('✅ Loading saved transition parameters:', paramsData.params)
              savedParams = paramsData.params
              
              // 应用保存的参数（除了nbeats，稍后处理以避免被syncInfo覆盖）
              if (paramsData.params.volumeCurve) setVolumeCurve(paramsData.params.volumeCurve)
              if (paramsData.params.eqCurve) setEQCurve(paramsData.params.eqCurve)
              if (paramsData.params.filterType) setFilterType(paramsData.params.filterType)
              if (paramsData.params.selectedPreset) setSelectedPreset(paramsData.params.selectedPreset)
              
              // 应用 offset 参数
              if (typeof paramsData.params.track1OffsetBeats === 'number') {
                setTrack1OffsetBeats(paramsData.params.track1OffsetBeats)
                console.log('✅ Loaded track1OffsetBeats:', paramsData.params.track1OffsetBeats)
              }
              if (typeof paramsData.params.track2OffsetBeats === 'number') {
                setTrack2OffsetBeats(paramsData.params.track2OffsetBeats)
                console.log('✅ Loaded track2OffsetBeats:', paramsData.params.track2OffsetBeats)
              }
              if (typeof paramsData.params.transitionOffsetBeats === 'number') {
                setTransitionOffsetBeats(paramsData.params.transitionOffsetBeats)
                console.log('✅ Loaded transitionOffsetBeats:', paramsData.params.transitionOffsetBeats)
              }
              
              console.log('✅ Transition parameters applied (nbeats will be applied after sync)')
            } else {
              console.log('ℹ️ No saved parameters found, using defaults')
            }
          }
        } catch (paramsError) {
          console.warn('⚠️ Failed to load saved parameters:', paramsError)
          // 继续执行，使用默认参数
        }
        
        if (cacheData.cacheExists) {
          // 使用缓存
          console.log('✅ Using cached transition')
          setPlaygroundTestResult(cacheData.message)
          setTransitionId(cacheData.transitionId)
          setOutputDir(cacheData.cachePath)
          setIsFromCache(true)
          
          // 只有当同时具备 playground 音频文件和同步信息时才加载播放器
          const hasPlaygroundFiles = cacheData.playgroundFiles && 
                                     cacheData.playgroundFiles.prevtrack && 
                                     cacheData.playgroundFiles.nexttrack
          const hasSyncInfo = cacheData.syncInfo && 
                             cacheData.syncInfo.nbeats && 
                             cacheData.syncInfo.track_prev_bpm
          
            if (hasPlaygroundFiles && hasSyncInfo) {
              console.log('📦 Loading playground audio files:', cacheData.playgroundFiles)
              console.log('📊 Loading sync info:', cacheData.syncInfo)
              
              setPlaygroundAudioFiles({
                prevtrack: cacheData.playgroundFiles.prevtrack || null,
                nexttrack: cacheData.playgroundFiles.nexttrack || null
              })
              
              setSyncInfo(cacheData.syncInfo)
              
              // 如果有保存的nbeats参数，优先使用；否则使用syncInfo中的nbeats
              if (savedParams && typeof savedParams.nbeats === 'number') {
                setCurrentNbeats(savedParams.nbeats)
                // 同时更新 transitionDuration 以保持一致
                const bars = savedParams.nbeats / 4
                const secondsPerBeat = 60 / fromTrack.features.bpm
                const duration = bars * 4 * secondsPerBeat
                setTransitionDuration(duration)
                console.log('✅ Applied saved nbeats:', savedParams.nbeats, 'bars:', bars, 'duration:', duration)
              } else {
                // 使用 syncInfo 中的 nbeats
                setCurrentNbeats(cacheData.syncInfo.nbeats)
                console.log('ℹ️ Using nbeats from syncInfo:', cacheData.syncInfo.nbeats)
              }
            } else {
            console.log('⚠️ Cache exists but missing playground files or sync info')
          }
          
          setIsLoadingTransition(false)
          return
        }
        
        // 没有缓存，生成新的转场
        console.log('🧪 No cache found, generating new transition')
        console.log('📡 Calling API:', `${getApiBaseUrl()}/api/transition-editor/test-playground`)
        console.log('📤 Request body:', { hashPrev, hashNext })
        
        const generateResponse = await fetch(`${getApiBaseUrl()}/api/transition-editor/test-playground`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            hashPrev,
            hashNext,
          })
        })
        
        if (!generateResponse.ok) {
          const errorData = await generateResponse.json()
          console.error('❌ Generate API error:', errorData)
          throw new Error(errorData.error || '生成失败')
        }
        
        const generateData = await generateResponse.json()
        console.log('✅ Generation result:', generateData)
        
        setPlaygroundTestResult(generateData.message)
        setTransitionId(generateData.transitionId)
        setOutputDir(generateData.outputDir || generateData.cacheDir)
        setIsFromCache(generateData.cached || false)
        
        // 生成成功后，重新检查缓存以获取 playground 文件
        try {
          const recheckResponse = await fetch(`${getApiBaseUrl()}/api/transition-editor/check-cache`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              hashPrev,
              hashNext,
            })
          })
          
          if (recheckResponse.ok) {
            const recheckData = await recheckResponse.json()
            console.log('📦 Recheck cache after generation:', recheckData)
            
            // 只有当同时具备 playground 音频文件和同步信息时才加载播放器
            const hasPlaygroundFiles = recheckData.playgroundFiles && 
                                       recheckData.playgroundFiles.prevtrack && 
                                       recheckData.playgroundFiles.nexttrack
            const hasSyncInfo = recheckData.syncInfo && 
                               recheckData.syncInfo.nbeats && 
                               recheckData.syncInfo.track_prev_bpm
            
            if (hasPlaygroundFiles && hasSyncInfo) {
              console.log('📦 Loading playground audio files after generation:', recheckData.playgroundFiles)
              console.log('📊 Loading sync info after generation:', recheckData.syncInfo)
              
              setPlaygroundAudioFiles({
                prevtrack: recheckData.playgroundFiles.prevtrack || null,
                nexttrack: recheckData.playgroundFiles.nexttrack || null
              })
              
              setSyncInfo(recheckData.syncInfo)
              
              // 生成新转场时，没有保存的参数，使用 syncInfo 中的 nbeats
              setCurrentNbeats(recheckData.syncInfo.nbeats)
              // 同时更新 transitionDuration
              const bars = recheckData.syncInfo.nbeats / 4
              const secondsPerBeat = 60 / fromTrack.features.bpm
              const duration = bars * 4 * secondsPerBeat
              setTransitionDuration(duration)
              console.log('ℹ️ Applied nbeats from syncInfo after generation:', recheckData.syncInfo.nbeats)
            }
          }
        } catch (recheckError) {
          console.warn('⚠️ Failed to recheck cache after generation:', recheckError)
        }
        
        // 生成完成后，将 isLoadingTransition 设置为 false
        setIsLoadingTransition(false)
        
      } catch (error) {
        console.error('❌ Error loading/generating transition:', error)
        const errorMessage = error instanceof Error ? error.message : '操作失败'
        setPlaygroundTestError(errorMessage)
        setIsLoadingTransition(false)
        
        // 清空 playground 数据，确保不显示播放器
        setPlaygroundAudioFiles({ prevtrack: null, nexttrack: null })
        setSyncInfo(null)
      }
    }
    
    loadOrGenerateTransition()
  }, [fromTrack, toTrack])
  
  // 改变转场长度
  const handleDurationChange = (bars: number) => {
    const beatsPerBar = 4
    const secondsPerBeat = 60 / fromTrack.features.bpm
    const duration = bars * beatsPerBar * secondsPerBeat
    setTransitionDuration(duration)
    
    // 同步更新 nbeats
    const newNbeats = bars * beatsPerBar
    setCurrentNbeats(newNbeats)
    console.log(`🎛️ 转场长度更新: ${bars} bars = ${newNbeats} beats = ${duration.toFixed(2)}s`)
  }
  
  // 应用预设
  const applyPreset = (preset: TransitionPreset) => {
    setSelectedPreset(preset)
    
    switch (preset) {
      case 'fade':
        setVolumeCurve('fade-in-fade-out')
        setEQCurve('center-bass-swap')
        setFilterType('none')
        break
      case 'rise':
        setVolumeCurve('overlap')
        setEQCurve('end-bass-swap')
        setFilterType('low-pass-out')
        break
      case 'blend':
        setVolumeCurve('overlap')
        setEQCurve('3-band-fade')
        setFilterType('none')
        break
      case 'wave':
        setVolumeCurve('overlap')
        setEQCurve('center-bass-swap')
        setFilterType('low-pass-in-out')
        break
      case 'melt':
        setVolumeCurve('fade-in-fade-out')
        setEQCurve('center-bass-swap')
        setFilterType('high-pass-in-out')
        break
      case 'slam':
        setVolumeCurve('center-cut')
        setEQCurve('none')
        setFilterType('none')
        break
      case 'auto':
      default:
        // Auto 根据歌曲特性智能选择
        const bpmDiff = Math.abs(fromTrack.features.bpm - toTrack.features.bpm)
        if (bpmDiff < 5) {
          setVolumeCurve('smooth-crossfade')
          setEQCurve('center-bass-swap')
          setFilterType('low-pass-in-out')
        } else {
          setVolumeCurve('fade-in-fade-out')
          setEQCurve('3-band-fade')
          setFilterType('none')
        }
        break
    }
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 顶部导航 */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              <ArrowLeft size={20} />
              返回歌单
            </button>
            
            {/* 教程按钮 */}
            <button
              onClick={() => setIsTutorialOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-all shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50"
              title="查看使用指南"
            >
              <HelpCircle className="h-5 w-5" />
              <span className="text-sm">如何体验</span>
            </button>
          </div>
          
          <div className="flex items-center gap-3">
            {/* MIDI 连接状态 */}
            <button 
              onClick={() => setShowMidiSettings(!showMidiSettings)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                midiConnected 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-gray-600 hover:bg-gray-700 text-white'
              }`}
              data-tutorial="midi-settings"
            >
              {midiConnected ? <Radio size={20} /> : <Usb size={20} />}
              <span>MIDI {midiConnected ? '已连接' : '设置'}</span>
            </button>
            
            <button
            data-tutorial="save-button"
            onClick={async () => {
              try {
                // 收集所有影响效果的参数
                const params = {
                  volumeCurve,
                  eqCurve,
                  filterType,
                  nbeats: currentNbeats,
                  selectedPreset,
                  transitionDuration,
                  // Offset 参数
                  track1OffsetBeats,
                  track2OffsetBeats,
                  transitionOffsetBeats,
                  // 可选：添加其他参数
                  track1EQ,
                  track2EQ,
                  track1Filter,
                  track2Filter,
                  track1Volume,
                  track2Volume,
                }
                
                // 从 URL 或 filename 中提取 hash
                const hashPrev = fromTrack.filename.replace(/\.(mp3|wav|flac)$/i, '')
                const hashNext = toTrack.filename.replace(/\.(mp3|wav|flac)$/i, '')
                
                console.log('💾 Saving transition parameters:', params)
                
                // 调用保存API
                const response = await fetch(`${getApiBaseUrl()}/api/transition-editor/save-params`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    hashPrev,
                    hashNext,
                    params
                  })
                })
                
                if (!response.ok) {
                  const errorData = await response.json()
                  throw new Error(errorData.error || '保存失败')
                }
                
                const data = await response.json()
                console.log('✅ Transition parameters saved:', data)
                
                navigate(-1)
              } catch (error) {
                console.error('❌ Error saving transition parameters:', error)
                alert(`保存失败: ${error instanceof Error ? error.message : '未知错误'}`)
              }
            }}
            className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
          >
            保存转场
          </button>
          </div>
        </div>
        
        {/* MIDI 设置面板 */}
        {showMidiSettings && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-6 border border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              MIDI 输出设置
            </h2>
            
            <div className="space-y-4">
              {/* MIDI 支持状态 */}
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${
                  midiSupported ? 'bg-green-500' : 'bg-red-500'
                }`} />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Web MIDI API: {midiSupported ? '支持' : '不支持'}
                </span>
              </div>
              
              {!midiSupported && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
                  <strong>浏览器不支持 Web MIDI API</strong><br/>
                  请使用 Chrome、Edge 或 Opera 浏览器访问此页面。
                </div>
              )}
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* 输入设备选择 */}
                <div>
                  <h3 className="text-sm font-semibold mb-2 text-blue-600 dark:text-blue-400">
                    输入设备 (控制 Tone.js 效果器)
                  </h3>
                  {midiInputs.length === 0 ? (
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-sm text-gray-600 dark:text-gray-400">
                      ❌ 未检测到 MIDI 输入设备
                    </div>
                  ) : (
                    <select
                      value={selectedInput || ''}
                      onChange={(e) => handleInputChange(e.target.value)}
                      className="w-full bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">选择输入设备...</option>
                      {midiInputs.map(input => (
                        <option key={input.id} value={input.id}>
                          {input.name || input.id}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                
                {/* 输出设备选择 */}
                <div>
                  <h3 className="text-sm font-semibold mb-2 text-green-600 dark:text-green-400">
                    输出设备 (发送 Envelope 控制)
                  </h3>
                  {midiOutputs.length === 0 ? (
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-sm text-gray-600 dark:text-gray-400">
                      ❌ 未检测到 MIDI 输出设备
                    </div>
                  ) : (
                    <select
                      value={selectedOutput || ''}
                      onChange={(e) => handleOutputChange(e.target.value)}
                      className="w-full bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">选择输出设备...</option>
                      {midiOutputs.map(output => (
                        <option key={output.id} value={output.id}>
                          {output.name || output.id}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              
              {/* MIDI 输入启用开关 */}
              <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div>
                  <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                    启用 MIDI 输入控制 (手动模式)
                  </p>
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    通过硬件控制器实时调整 Tone.js 效果器参数 · 使用 CC114 切换
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-blue-700 dark:text-blue-300 font-mono">
                    CC114: {midiInputEnabled ? '0' : '127'}
                  </span>
                  <button
                    onClick={() => {
                      setMidiInputEnabled(!midiInputEnabled)
                      setMidiEnabled(midiInputEnabled) // 切换输出状态
                    }}
                    disabled={!selectedInput}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      midiInputEnabled 
                        ? 'bg-blue-600' 
                        : 'bg-gray-300 dark:bg-gray-600'
                    } ${!selectedInput ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        midiInputEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
              
              {/* MIDI 输出启用开关 */}
              <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <div>
                  <p className="text-sm font-semibold text-green-900 dark:text-green-100">
                    启用 MIDI 输出 (自动模式) · 默认启用
                  </p>
                  <p className="text-xs text-green-700 dark:text-green-300">
                    播放时根据 Envelope 实时发送 MIDI CC 消息 · 使用 CC114 切换
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-green-700 dark:text-green-300 font-mono">
                    CC114: {midiEnabled ? '127' : '0'}
                  </span>
                  <button
                    onClick={() => {
                      setMidiEnabled(!midiEnabled)
                      setMidiInputEnabled(midiEnabled) // 切换输入状态
                    }}
                    disabled={!midiConnected}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      midiEnabled 
                        ? 'bg-green-600' 
                        : 'bg-gray-300 dark:bg-gray-600'
                    } ${!midiConnected ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        midiEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
              
              {/* MIDI CC 映射说明 */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
                  MIDI CC 映射
                </h4>
                
                {/* 模式切换说明 */}
                <div className="mb-3 p-2 bg-white dark:bg-gray-800 rounded border border-blue-300 dark:border-blue-700">
                  <p className="text-xs font-semibold text-blue-900 dark:text-blue-100 mb-1">
                    🎛️ CC114: 模式切换
                  </p>
                  <div className="text-xs text-blue-800 dark:text-blue-200 space-y-0.5">
                    <div>• <strong>127</strong> = 自动模式 (启用输出，禁用输入)</div>
                    <div>• <strong>0</strong> = 手动模式 (启用输入，禁用输出)</div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-xs text-blue-800 dark:text-blue-200">
                  <div>
                    <strong className="block mb-1">Track 1 (Deck 0):</strong>
                    <div className="space-y-0.5 font-mono">
                      <div>CC{CC_MAP.channel1.volume}: Volume</div>
                      <div>CC{CC_MAP.channel1.eqHigh}: EQ High</div>
                      <div>CC{CC_MAP.channel1.eqMid}: EQ Mid</div>
                      <div>CC{CC_MAP.channel1.eqLow}: EQ Low</div>
                      <div>CC{CC_MAP.channel1.filter}: Filter</div>
                    </div>
                  </div>
                  <div>
                    <strong className="block mb-1">Track 2 (Deck 1):</strong>
                    <div className="space-y-0.5 font-mono">
                      <div>CC{CC_MAP.channel2.volume}: Volume</div>
                      <div>CC{CC_MAP.channel2.eqHigh}: EQ High</div>
                      <div>CC{CC_MAP.channel2.eqMid}: EQ Mid</div>
                      <div>CC{CC_MAP.channel2.eqLow}: EQ Low</div>
                      <div>CC{CC_MAP.channel2.filter}: Filter</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* 主编辑区 */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
          {/* 歌曲信息头部 */}
          <div className="grid grid-cols-2 gap-px bg-gray-200 dark:bg-gray-700">
            {/* 前一首歌 */}
            <div className="bg-white dark:bg-gray-800 p-6">
              <div className="flex items-start gap-4">
                <img
                  src={getAlbumCoverUrl(fromTrack.albumCover) || '/placeholder-album.svg'}
                  alt={fromTrack.name}
                  className="w-16 h-16 rounded-lg object-cover bg-gray-200 dark:bg-gray-700"
                  onError={(e) => {
                    // 防止无限循环：只设置一次placeholder
                    if (e.currentTarget.src !== window.location.origin + '/placeholder-album.svg') {
                      e.currentTarget.src = '/placeholder-album.svg'
                    }
                  }}
                />
                <div className="flex-1">
                  <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100 mb-1">
                    {fromTrack.name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {fromTrack.artist}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs px-2 py-1 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold">
                      {fromTrack.features.bpm} BPM
                    </span>
                    <span className="text-xs px-2 py-1 rounded-md bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-semibold">
                      {toCamelotKey(fromTrack.features.key, fromTrack.features.keyMode)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* 后一首歌 */}
            <div className="bg-white dark:bg-gray-800 p-6">
              <div className="flex items-start gap-4">
                <img
                  src={getAlbumCoverUrl(toTrack.albumCover) || '/placeholder-album.svg'}
                  alt={toTrack.name}
                  className="w-16 h-16 rounded-lg object-cover bg-gray-200 dark:bg-gray-700"
                  onError={(e) => {
                    // 防止无限循环：只设置一次placeholder
                    if (e.currentTarget.src !== window.location.origin + '/placeholder-album.svg') {
                      e.currentTarget.src = '/placeholder-album.svg'
                    }
                  }}
                />
                <div className="flex-1">
                  <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100 mb-1">
                    {toTrack.name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {toTrack.artist}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs px-2 py-1 rounded-md bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-semibold">
                      {toTrack.features.bpm} BPM
                    </span>
                    <span className="text-xs px-2 py-1 rounded-md bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:teal-purple-300 font-semibold">
                      {toCamelotKey(toTrack.features.key, toTrack.features.keyMode)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Playground Mode 加载/缓存状态 */}
          {isLoadingTransition && (
            <div className="border-b bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 px-6 py-3">
              <div className="flex items-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" />
                <span className="text-blue-900 dark:text-blue-100 font-medium">
                  正在检查缓存并加载转场...
                </span>
              </div>
            </div>
          )}
          
          {/* Playground Mode 测试结果 */}
          {/* {!isLoadingTransition && (playgroundTestResult || playgroundTestError) && (
            <div className={`border-b px-6 py-3 ${
              playgroundTestError 
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            }`}>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <span className={`font-medium ${
                      playgroundTestError
                        ? 'text-red-900 dark:text-red-100'
                        : 'text-green-900 dark:text-green-100'
                    }`}>
                      {playgroundTestError ? '❌' : isFromCache ? '📦' : '✅'} {playgroundTestError || playgroundTestResult}
                      {isFromCache && ' (来自缓存)'}
                    </span>
                  </div>
                  {!playgroundTestError && isFromCache && (
                    <button
                      onClick={handleForceRegenerate}
                      disabled={isGenerating}
                      className="text-xs px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isGenerating ? '重新生成中...' : '🔄 重新生成'}
                    </button>
                  )}
                </div>
                {transitionId && (
                  <div className="text-xs text-green-700 dark:text-green-300 font-mono">
                    🔗 Transition ID: {transitionId}
                  </div>
                )}
                {outputDir && (
                  <div className="text-xs text-green-700 dark:text-green-300 font-mono">
                    📁 Output: {outputDir}
                  </div>
                )}
              </div>
            </div>
          )} */}
          
          {/* 波形显示区 */}
          <div className="p-6 space-y-6" data-tutorial="waveform-player">
            {/* 如果有 playground 音频文件和同步信息，显示 playground 播放器 */}
            {playgroundAudioFiles.prevtrack && playgroundAudioFiles.nexttrack && syncInfo ? (
              <div key={`${playgroundAudioFiles.prevtrack}-${playgroundAudioFiles.nexttrack}`}>
                <PlaygroundTransitionPlayer
                  prevtrackUrl={playgroundAudioFiles.prevtrack}
                  nexttrackUrl={playgroundAudioFiles.nexttrack}
                  trackPrevBpm={syncInfo.track_prev_bpm}
                  trackNextStartBeat={syncInfo.track_next_start_beat}
                  nbeats={currentNbeats}
                  height={120}
                  track1EQ={track1EQ}
                  track2EQ={track2EQ}
                  track1Filter={track1Filter}
                  track2Filter={track2Filter}
                  track1Volume={track1Volume}
                  track2Volume={track2Volume}
                  volumeCurvePreset={volumeCurve}
                  eqCurvePreset={eqCurve}
                  filterTypePreset={filterType}
                  initialTrack1OffsetBeats={track1OffsetBeats}
                  initialTrack2OffsetBeats={track2OffsetBeats}
                  initialTransitionOffsetBeats={transitionOffsetBeats}
                  onOffsetChange={(track1, track2, transition) => {
                    setTrack1OffsetBeats(track1)
                    setTrack2OffsetBeats(track2)
                    setTransitionOffsetBeats(transition)
                  }}
                  onEnvelopeUpdate={handleEnvelopeUpdate}
                />
              </div>
            ) : (
              <>
                {/* 加载中或生成中的占位界面 */}
                <div className="flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 rounded-lg border-2 border-dashed border-blue-300 dark:border-blue-700 p-12" style={{ minHeight: '400px' }}>
                  {playgroundTestError ? (
                    // 错误状态
                    <div className="text-center space-y-6 max-w-2xl">
                      {/* 错误图标 */}
                      <div className="relative w-24 h-24 mx-auto">
                        <div className="absolute inset-0 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                          <svg className="w-12 h-12 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                      </div>
                      
                      {/* 错误标题 */}
                      <div className="space-y-3">
                        <h3 className="text-2xl font-bold text-red-600 dark:text-red-400">
                          ❌ Playground 生成失败
                        </h3>
                        <p className="text-base text-gray-700 dark:text-gray-300">
                          无法为这两首歌曲生成转场数据
                        </p>
                      </div>
                      
                      {/* 错误详情 */}
                      <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-lg p-4">
                        <p className="text-sm text-red-800 dark:text-red-200 font-mono break-words">
                          {playgroundTestError}
                        </p>
                      </div>
                      
                      {/* 建议操作 */}
                      <div className="space-y-4">
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                          <p className="text-sm text-yellow-800 dark:text-yellow-200">
                            <strong>💡 建议：</strong>
                          </p>
                          <ul className="text-sm text-yellow-700 dark:text-yellow-300 mt-2 space-y-1 text-left list-disc list-inside">
                            <li>尝试选择其他歌曲组合</li>
                            <li>确保两首歌曲的 BPM 和音调信息准确</li>
                            <li>检查歌曲文件是否完整且未损坏</li>
                            <li>查看服务器日志获取更多详细信息</li>
                          </ul>
                        </div>
                        
                        {/* 操作按钮 */}
                        <div className="flex items-center justify-center gap-3">
                          <button
                            onClick={() => navigate(-1)}
                            className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors shadow-md hover:shadow-lg"
                          >
                            ← 返回歌单
                          </button>
                          <button
                            onClick={() => {
                              setPlaygroundTestError(null)
                              setIsLoadingTransition(true)
                              window.location.reload()
                            }}
                            className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors shadow-md hover:shadow-lg"
                          >
                            🔄 重试
                          </button>
                        </div>
                      </div>
                      
                      {/* 提示信息 */}
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        如果问题持续存在，请联系技术支持或尝试不同的歌曲组合
                      </p>
                    </div>
                  ) : (
                    // 加载状态
                    <div className="text-center space-y-6">
                    {/* 主加载动画 */}
                    <div className="relative w-24 h-24 mx-auto">
                      {/* 外圈旋转 */}
                      <div className="absolute inset-0 rounded-full border-4 border-blue-200 dark:border-blue-900"></div>
                      <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-600 dark:border-t-blue-400 animate-spin"></div>
                      
                      {/* 内圈反向旋转 */}
                      <div className="absolute inset-2 rounded-full border-4 border-purple-200 dark:border-purple-900"></div>
                      <div className="absolute inset-2 rounded-full border-4 border-transparent border-b-purple-600 dark:border-b-purple-400 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1s' }}></div>
                      
                      {/* 中心音符图标 */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <svg className="w-10 h-10 text-blue-600 dark:text-blue-400 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                        </svg>
                      </div>
                    </div>
                    
                    {/* 加载文本 */}
                    <div className="space-y-2">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                        {isLoadingTransition ? '🔍 正在检查缓存...' : '🎵 正在生成 Playground 数据...'}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                        {isLoadingTransition 
                          ? '正在检查是否存在缓存的转场数据' 
                          : playgroundTestError 
                          ? '生成失败，请检查日志' 
                          : '正在使用 automix_cli 生成转场数据，这可能需要几秒钟...'}
                      </p>
                    </div>
                    
                    {/* 加载状态指示器 */}
                    <div className="flex flex-col items-center gap-3 pt-4">
                      <div className="flex items-center gap-2 text-sm">
                        <div className={`w-2.5 h-2.5 rounded-full ${!isLoadingTransition ? 'bg-green-500 animate-pulse' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
                        <span className={!isLoadingTransition ? 'text-green-600 dark:text-green-400 font-medium' : 'text-gray-500 dark:text-gray-400'}>
                          {!isLoadingTransition ? '✓ 缓存检查完成' : '⏳ 检查缓存中...'}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm">
                        <div className={`w-2.5 h-2.5 rounded-full ${playgroundAudioFiles.prevtrack && playgroundAudioFiles.nexttrack ? 'bg-green-500 animate-pulse' : !isLoadingTransition ? 'bg-yellow-500 animate-pulse' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
                        <span className={playgroundAudioFiles.prevtrack && playgroundAudioFiles.nexttrack ? 'text-green-600 dark:text-green-400 font-medium' : !isLoadingTransition ? 'text-yellow-600 dark:text-yellow-400 font-medium' : 'text-gray-500 dark:text-gray-400'}>
                          {playgroundAudioFiles.prevtrack && playgroundAudioFiles.nexttrack 
                            ? '✓ Playground 数据已加载' 
                            : !isLoadingTransition 
                            ? '⏳ 生成 Playground 数据中...' 
                            : '等待生成 Playground 数据'}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm">
                        <div className={`w-2.5 h-2.5 rounded-full ${syncInfo ? 'bg-green-500 animate-pulse' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
                        <span className={syncInfo ? 'text-green-600 dark:text-green-400 font-medium' : 'text-gray-500 dark:text-gray-400'}>
                          {syncInfo ? '✓ 同步信息已加载' : '等待同步信息'}
                        </span>
                      </div>
                    </div>
                    
                    {/* 进度条 */}
                    <div className="w-80 mx-auto">
                      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-all duration-500 ease-out"
                          style={{ 
                            width: `${(
                              (!isLoadingTransition ? 33 : 0) + 
                              ((playgroundAudioFiles.prevtrack && playgroundAudioFiles.nexttrack) ? 33 : 0) + 
                              (syncInfo ? 34 : 0)
                            )}%`
                          }}
                        ></div>
                      </div>
                    </div>
                    
                    {/* 提示信息 */}
                    <p className="text-xs text-gray-500 dark:text-gray-500 max-w-md mx-auto">
                      💡 系统正在使用 CLI 工具生成转场数据，完成后将自动加载播放器
                    </p>
                  </div>
                  )}
                </div>
              </>
            )}
          </div>
          
          {/* 转场长度选择 */}
          <div className="px-6 pb-6" data-tutorial="duration-control">
            <div className="flex items-center justify-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400 mr-2">转场长度:</span>
              {[1, 2, 4, 8, 16].map(bars => {
                // 计算当前 nbeats 对应的实际 bars 长度
                const currentBars = Math.round(currentNbeats / 4)
                const isCurrentLength = currentBars === bars
                
                return (
                  <button
                    key={bars}
                    onClick={() => handleDurationChange(bars)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors relative ${
                      isCurrentLength
                        ? 'bg-green-500 text-white shadow-lg ring-2 ring-green-400 ring-opacity-50'
                        : Math.abs(transitionDuration - (bars * 4 * 60 / fromTrack.features.bpm)) < 0.5
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {bars} Bars
                    {isCurrentLength && (
                      <span className="absolute -top-1 -right-1 flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                      </span>
                    )}
                  </button>
                )
              })}
              <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                (当前: {Math.round(currentNbeats / 4)} bars / {currentNbeats} beats)
              </span>
            </div>
          </div>

          {/* 高级控制 */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-6" data-tutorial="advanced-controls">
            <div className="grid grid-cols-3 gap-6">
              {/* 音量曲线 */}
              <div className="flex flex-col items-center">
                <label className="text-xs font-medium text-purple-600 dark:text-purple-400 mb-2">
                  Volume
                </label>
                <select
                  value={volumeCurve}
                  onChange={(e) => {
                    setVolumeCurve(e.target.value as VolumeCurve)
                    e.currentTarget.blur()
                  }}
                  className="w-full px-3 py-2 rounded-lg bg-purple-50 dark:bg-purple-900/20 text-gray-900 dark:text-gray-100 text-sm border border-purple-200 dark:border-purple-800 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="smooth-crossfade">Smooth Crossfade</option>
                  <option value="overlap">Overlap</option>
                  <option value="fade-in-fade-out">Fade In Fade Out</option>
                  <option value="cut-in-fade-out">Cut In Fade Out</option>
                  <option value="fade-in-cut-out">Fade In Cut Out</option>
                  <option value="center-cut">Center Cut</option>
                  <option value="crossfade">Crossfade</option>
                </select>
              </div>
              
              {/* EQ曲线 */}
              <div className="flex flex-col items-center">
                <label className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-2">
                  EQ
                </label>
                <select
                  value={eqCurve}
                  onChange={(e) => {
                    setEQCurve(e.target.value as EQCurve)
                    e.currentTarget.blur()
                  }}
                  className="w-full px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-gray-900 dark:text-gray-100 text-sm border border-blue-200 dark:border-blue-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="center-bass-swap">Center Bass Swap</option>
                  <option value="end-bass-swap">End Bass Swap</option>
                  <option value="3-band-fade">3 Band Fade</option>
                  <option value="quick-bass-cut">Quick Bass Cut</option>
                  <option value="long-bass-cut">Long Bass Cut</option>
                  <option value="none">None</option>
                </select>
              </div>
              
              {/* 滤波器 */}
              <div className="flex flex-col items-center">
                <label className="text-xs font-medium text-emerald-600 dark:text-emerald-400 mb-2">
                  Filter
                </label>
                <select
                  value={filterType}
                  onChange={(e) => {
                    setFilterType(e.target.value as FilterType)
                    e.currentTarget.blur()
                  }}
                  className="w-full px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-gray-900 dark:text-gray-100 text-sm border border-emerald-200 dark:border-emerald-800 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="none">None</option>
                  <option value="low-pass-out">Low Pass Out</option>
                  <option value="low-pass-in">Low Pass In</option>
                  <option value="low-pass-in-out">Low Pass In Out</option>
                  <option value="high-pass-out">High Pass Out</option>
                  <option value="high-pass-in">High Pass In</option>
                  <option value="high-pass-in-out">High Pass In Out</option>
                  <option value="low-pass-in-low-pass-out">Low Pass In Low Pass Out</option>
                  <option value="low-pass-in-high-pass-out">Low Pass In High Pass Out</option>
                  <option value="high-pass-in-low-pass-out">High Pass In Low Pass Out</option>
                  <option value="high-pass-in-high-pass-out">High Pass In High Pass Out</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* 预设样式选择 */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-6" data-tutorial="presets">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
              转场预设
            </h3>
            <div className="grid grid-cols-7 gap-2">
              {(['auto', 'fade', 'rise', 'blend', 'wave', 'melt', 'slam'] as TransitionPreset[]).map(preset => (
                <button
                  key={preset}
                  onClick={() => applyPreset(preset)}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    selectedPreset === preset
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {preset === 'auto' && 'Auto'}
                  {preset === 'fade' && 'Fade'}
                  {preset === 'rise' && 'Rise'}
                  {preset === 'blend' && 'Blend'}
                  {preset === 'wave' && 'Wave'}
                  {preset === 'melt' && 'Melt'}
                  {preset === 'slam' && 'Slam'}
                </button>
              ))}
            </div>
          </div>
          
          
        </div>
        
        {/* 效果曲线可视化 */}
        {/* 
        <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6">
            效果曲线可视化
          </h2>
          <TransitionControls
            volumeCurve={volumeCurve}
            eqCurve={eqCurve}
            filterType={filterType}
            duration={transitionDuration}
          />
        </div>
        */}
      </div>
      
      {/* 教程组件 */}
      <AppTutorial
        isOpen={isTutorialOpen}
        onClose={() => setIsTutorialOpen(false)}
        steps={tutorialSteps}
      />
    </div>
  )
}

